/**
 * Feature Access Hooks
 *
 * React hooks for checking feature access in components.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import type { PdfPolicy } from '../utils/pdfExport';
import {
  fetchFeatureAccess,
  selectHasFeatureAccess,
  selectFeatureAccessResult,
  selectFeaturesLoading,
  selectFeaturesError,
  selectAccessibleFeatureIds,
  selectLockedFeatures,
  selectFeatureAccess,
  selectIsLaunchPromo,
} from '../store/featuresSlice';

/**
 * Hook to check if user has access to a specific feature
 *
 * @param featureId - The feature ID to check
 * @returns Object with hasAccess boolean, loading state, and access details
 *
 * @example
 * ```tsx
 * const { hasAccess, isLoading, accessReason } = useFeatureAccess('deal_analyzer');
 *
 * if (isLoading) return <Spinner />;
 * if (!hasAccess) return <UpgradePrompt />;
 * return <DealAnalyzer />;
 * ```
 */
export function useFeatureAccess(featureId: string): {
  hasAccess: boolean;
  isLoading: boolean;
  error: string | null;
  accessReason: string | null;
  upgradeRequired: string | null;
  expiresAt: string | null;
  isLaunchPromo: boolean;
  value: any | null;
  parentLocked: boolean;
} {
  const dispatch = useAppDispatch();
  const hasAccess = useAppSelector(selectHasFeatureAccess(featureId));
  const accessResult = useAppSelector(selectFeatureAccessResult(featureId));
  const isLoading = useAppSelector(selectFeaturesLoading);
  const error = useAppSelector(selectFeaturesError);
  const featureAccess = useAppSelector(selectFeatureAccess);
  const isLaunchPromo = useAppSelector(selectIsLaunchPromo);

  // Fetch features if not loaded
  useEffect(() => {
    if (!featureAccess && !isLoading && !error) {
      dispatch(fetchFeatureAccess(false));
    }
  }, [dispatch, featureAccess, isLoading, error]);

  return {
    hasAccess,
    isLoading,
    error,
    accessReason: accessResult?.accessReason || null,
    upgradeRequired: accessResult?.upgradeRequired || null,
    expiresAt: accessResult?.expiresAt || null,
    isLaunchPromo,
    value: accessResult?.value ?? null,
    parentLocked: accessResult?.parentLocked ?? false,
  };
}

/**
 * Hook to get access details for multiple features at once
 *
 * @param featureIds - Array of feature IDs to check
 * @returns Object mapping feature IDs to their access status
 *
 * @example
 * ```tsx
 * const access = useMultiFeatureAccess(['deal_analyzer', 'csv_export', 'pdf_export']);
 *
 * if (access.deal_analyzer.hasAccess) {
 *   // Show deal analyzer
 * }
 * ```
 */
export function useMultiFeatureAccess(featureIds: string[]): {
  isLoading: boolean;
  error: string | null;
  access: Record<string, { hasAccess: boolean; upgradeRequired?: string }>;
} {
  const featureAccess = useAppSelector(selectFeatureAccess);
  const isLoading = useAppSelector(selectFeaturesLoading);
  const error = useAppSelector(selectFeaturesError);

  const access = useMemo(() => {
    const result: Record<string, { hasAccess: boolean; upgradeRequired?: string }> = {};

    for (const featureId of featureIds) {
      const feature = featureAccess?.features[featureId];
      result[featureId] = {
        hasAccess: feature?.hasAccess ?? false,
        upgradeRequired: feature?.upgradeRequired,
      };
    }

    return result;
  }, [featureAccess, featureIds]);

  return {
    isLoading,
    error,
    access,
  };
}

/**
 * Hook to get a feature gate - returns state and action for gating features
 *
 * @param featureId - The feature ID to gate
 * @returns Object with isEnabled, isLocked, and methods to check/refresh
 *
 * @example
 * ```tsx
 * const gate = useFeatureGate('csv_export');
 *
 * return (
 *   <Button
 *     onClick={gate.isEnabled ? handleExport : gate.showUpgradePrompt}
 *     disabled={gate.isLocked}
 *   >
 *     {gate.isLocked ? 'Upgrade to Export' : 'Export CSV'}
 *   </Button>
 * );
 * ```
 */
export function useFeatureGate(featureId: string): {
  isEnabled: boolean;
  isLocked: boolean;
  isLoading: boolean;
  upgradeRequired: string | null;
  accessReason: string | null;
  refresh: () => void;
} {
  const dispatch = useAppDispatch();
  const { hasAccess, isLoading, accessReason, upgradeRequired } = useFeatureAccess(featureId);

  const refresh = useCallback(() => {
    dispatch(fetchFeatureAccess(true));
  }, [dispatch]);

  return {
    isEnabled: hasAccess,
    isLocked: !hasAccess && !isLoading,
    isLoading,
    upgradeRequired,
    accessReason,
    refresh,
  };
}

/**
 * Hook to get all accessible features
 *
 * @returns Array of feature IDs that the user has access to
 *
 * @example
 * ```tsx
 * const accessibleFeatures = useAccessibleFeatures();
 *
 * const showAdvancedOptions = accessibleFeatures.includes('advanced_filters');
 * ```
 */
export function useAccessibleFeatures(): string[] {
  const dispatch = useAppDispatch();
  const featureAccess = useAppSelector(selectFeatureAccess);
  const isLoading = useAppSelector(selectFeaturesLoading);
  const error = useAppSelector(selectFeaturesError);

  useEffect(() => {
    if (!featureAccess && !isLoading && !error) {
      dispatch(fetchFeatureAccess(false));
    }
  }, [dispatch, featureAccess, isLoading, error]);

  return useAppSelector(selectAccessibleFeatureIds);
}

/**
 * Hook to get all locked features with upgrade info
 *
 * @returns Array of locked features with their upgrade requirements
 *
 * @example
 * ```tsx
 * const lockedFeatures = useLockedFeatures();
 *
 * return (
 *   <LockedFeaturesList>
 *     {lockedFeatures.map(f => (
 *       <div key={f.featureId}>
 *         {f.featureId} - Requires {f.upgradeRequired}
 *       </div>
 *     ))}
 *   </LockedFeaturesList>
 * );
 * ```
 */
export function useLockedFeatures(): Array<{
  featureId: string;
  upgradeRequired?: string;
}> {
  const dispatch = useAppDispatch();
  const featureAccess = useAppSelector(selectFeatureAccess);
  const isLoading = useAppSelector(selectFeaturesLoading);
  const error = useAppSelector(selectFeaturesError);

  useEffect(() => {
    if (!featureAccess && !isLoading && !error) {
      dispatch(fetchFeatureAccess(false));
    }
  }, [dispatch, featureAccess, isLoading, error]);

  return useAppSelector(selectLockedFeatures);
}

/**
 * Hook to initialize feature access on login
 * Should be called once in the app root or auth context
 *
 * @example
 * ```tsx
 * function App() {
 *   useInitializeFeatures();
 *   return <Routes />;
 * }
 * ```
 */
export function useInitializeFeatures(): {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const dispatch = useAppDispatch();
  const featureAccess = useAppSelector(selectFeatureAccess);
  const isLoading = useAppSelector(selectFeaturesLoading);
  const error = useAppSelector(selectFeaturesError);

  useEffect(() => {
    // Only fetch if not already loaded and not currently loading
    if (!featureAccess && !isLoading && !error) {
      dispatch(fetchFeatureAccess(false));
    }
  }, [dispatch, featureAccess, isLoading, error]);

  const refresh = useCallback(() => {
    dispatch(fetchFeatureAccess(true));
  }, [dispatch]);

  return {
    isLoaded: !!featureAccess,
    isLoading,
    error,
    refresh,
  };
}

/**
 * Hook to check if launch promo is active
 *
 * @returns boolean indicating if launch promo is active
 *
 * @example
 * ```tsx
 * const isLaunchPromo = useLaunchPromo();
 * if (isLaunchPromo) {
 *   // Show promo banner or bypass tier checks
 * }
 * ```
 */
export function useLaunchPromo(): boolean {
  return useAppSelector(selectIsLaunchPromo);
}

// ============================================================================
// Capability-Aware Hooks (Phase 3)
// These extend the existing hooks with value resolution and policy support.
// ============================================================================

/**
 * Hook to get the resolved value for a non-boolean capability.
 * Returns the tier-specific value (e.g., number of months, max rows).
 *
 * @example
 * ```tsx
 * const crimeMonths = useCapabilityValue<number>('data.history.months.crime');
 * // STARTER: 6, PRO: 24, ELITE: 60, UNLIMITED: 120
 *
 * const maxRows = useCapabilityValue<number>('export.csv.max_rows');
 * // PRO: 250, ELITE: 1000, UNLIMITED: 10000
 * ```
 */
export function useCapabilityValue<T = any>(featureId: string): T | null {
  const dispatch = useAppDispatch();
  const featureAccess = useAppSelector(selectFeatureAccess);
  const isLoading = useAppSelector(selectFeaturesLoading);
  const error = useAppSelector(selectFeaturesError);
  const accessResult = useAppSelector(selectFeatureAccessResult(featureId));

  useEffect(() => {
    if (!featureAccess && !isLoading && !error) {
      dispatch(fetchFeatureAccess(false));
    }
  }, [dispatch, featureAccess, isLoading, error]);

  return (accessResult?.value as T) ?? null;
}

/**
 * Hook to check capability access with full details including value and parent lock.
 * Superset of useFeatureAccess — includes value and parentLocked.
 *
 * @example
 * ```tsx
 * const cap = useCapability('export.pdf.section.crime');
 * if (cap.parentLocked) {
 *   // Parent (export.pdf) is locked — show upgrade to unlock PDF first
 * } else if (!cap.hasAccess) {
 *   // This section specifically is locked
 * }
 * ```
 */
export function useCapability(featureId: string): {
  hasAccess: boolean;
  isLoading: boolean;
  value: any | null;
  accessReason: string | null;
  upgradeRequired: string | null;
  parentLocked: boolean;
} {
  const { hasAccess, isLoading, accessReason, upgradeRequired, value, parentLocked } = useFeatureAccess(featureId);

  return {
    hasAccess,
    isLoading,
    value,
    accessReason,
    upgradeRequired,
    parentLocked,
  };
}

/**
 * Hook to check multiple capabilities at once with values.
 *
 * @example
 * ```tsx
 * const caps = useCapabilities([
 *   'export.csv', 'export.excel', 'export.pdf',
 *   'data.history.months.crime',
 * ]);
 * if (caps['export.csv'].hasAccess) { ... }
 * const crimeMonths = caps['data.history.months.crime'].value;
 * ```
 */
export function useCapabilities(featureIds: string[]): Record<string, {
  hasAccess: boolean;
  value: any | null;
  upgradeRequired?: string;
  parentLocked: boolean;
}> {
  const dispatch = useAppDispatch();
  const featureAccess = useAppSelector(selectFeatureAccess);
  const isLoading = useAppSelector(selectFeaturesLoading);
  const error = useAppSelector(selectFeaturesError);

  useEffect(() => {
    if (!featureAccess && !isLoading && !error) {
      dispatch(fetchFeatureAccess(false));
    }
  }, [dispatch, featureAccess, isLoading, error]);

  return useMemo(() => {
    const result: Record<string, { hasAccess: boolean; value: any | null; upgradeRequired?: string; parentLocked: boolean }> = {};
    for (const featureId of featureIds) {
      const feature = featureAccess?.features[featureId];
      result[featureId] = {
        hasAccess: feature?.hasAccess ?? false,
        value: feature?.value ?? null,
        upgradeRequired: feature?.upgradeRequired,
        parentLocked: feature?.parentLocked ?? false,
      };
    }
    return result;
  }, [featureAccess, featureIds]);
}

/**
 * Type-safe feature ID constants
 * Use these to avoid typos in feature IDs
 */
export const FEATURE_IDS = {
  // Legacy IDs (kept for backwards compatibility — resolve via backend aliases)
  MAP_AMENITIES_BASIC: 'map_amenities_basic',
  MAP_AMENITIES_TRANSPORT: 'map_amenities_transport',
  MAP_AMENITIES_CRIME: 'map_amenities_crime',
  MAP_AMENITIES_FULL: 'map_amenities_full',
  DEAL_ANALYZER: 'deal_analyzer',
  STRATEGY_BTL: 'strategy_btl',
  STRATEGY_FLIP: 'strategy_flip',
  STRATEGY_HMO: 'strategy_hmo',
  STRATEGY_RENT_TO_RENT: 'strategy_rent_to_rent',
  STRATEGY_BRRR: 'strategy_brrr',
  STRATEGY_BMV: 'strategy_bmv',
  CSV_EXPORT: 'csv_export',
  PDF_EXPORT: 'pdf_export',
  ADVANCED_FILTERS: 'advanced_filters',
  EMAIL_ALERTS: 'email_alerts',
  WHATSAPP_ALERTS: 'whatsapp_alerts',
  API_ACCESS: 'api_access',
  PORTFOLIO_TRACKING: 'portfolio_tracking',
  ROI_PROJECTIONS: 'roi_projections',
  OFFMARKET_DEALS: 'offmarket_deals',
  BETA_FEATURES: 'beta_features',

  // Namespaced Dashboard Insights
  DASHBOARD_DEAL_CONCENTRATION: 'dashboard.insight.deal_concentration',
  DASHBOARD_MARKET_COVERAGE: 'dashboard.insight.market_coverage',
  DASHBOARD_STRATEGY_HOTSPOTS: 'dashboard.insight.strategy_hotspots',
  DASHBOARD_RETURNS_BY_LOCATION: 'dashboard.insight.returns_by_location',
  DASHBOARD_PORTFOLIO_BLIND_SPOTS: 'dashboard.insight.portfolio_blind_spots',
  DASHBOARD_VALUE_FINDER: 'dashboard.insight.value_finder',
  DASHBOARD_STRATEGY_BY_LOCATION: 'dashboard.insight.strategy_by_location',
  DASHBOARD_TOP_OPPORTUNITIES: 'dashboard.insight.top_opportunities',

  // Namespaced Analyzer
  ANALYZER_PAGE: 'analyzer.page',
  ANALYZER_STRATEGY_BTL: 'analyzer.strategy.btl',
  ANALYZER_STRATEGY_FLIP: 'analyzer.strategy.flip',
  ANALYZER_STRATEGY_HMO: 'analyzer.strategy.hmo',
  ANALYZER_STRATEGY_BRRR: 'analyzer.strategy.brrr',
  ANALYZER_STRATEGY_BMV: 'analyzer.strategy.bmv',
  ANALYZER_STRATEGY_RENT_TO_RENT: 'analyzer.strategy.rent_to_rent',
  ANALYZER_STRATEGY_R2SA: 'analyzer.strategy.r2sa',

  // Namespaced Exports
  EXPORT_CSV: 'export.csv',
  EXPORT_EXCEL: 'export.excel',
  EXPORT_PDF: 'export.pdf',
  EXPORT_PDF_SECTION_PROPERTY_OVERVIEW: 'export.pdf.section.property_overview',
  EXPORT_PDF_SECTION_QUALITY_SCORE: 'export.pdf.section.quality_score',
  EXPORT_PDF_SECTION_STRATEGY_ANALYSIS: 'export.pdf.section.strategy_analysis',
  EXPORT_PDF_SECTION_LOCATION_ANALYSIS: 'export.pdf.section.location_analysis',
  EXPORT_PDF_SECTION_CRIME: 'export.pdf.section.crime',
  EXPORT_PDF_SECTION_TRANSPORT: 'export.pdf.section.transport',
  EXPORT_PDF_SECTION_FLOOD: 'export.pdf.section.flood',
  EXPORT_PDF_SECTION_LAND_REGISTRY: 'export.pdf.section.land_registry',
  EXPORT_PDF_SECTION_ORIGINAL_LISTING: 'export.pdf.section.original_listing',
  EXPORT_PDF_SECTION_DISCLAIMER: 'export.pdf.section.disclaimer',
  EXPORT_PDF_MAX_PAGES: 'export.pdf.max_pages',
  EXPORT_PDF_UNWATERMARKED: 'export.pdf.unwatermarked',
  EXPORT_CSV_MAX_ROWS: 'export.csv.max_rows',

  // Namespaced Historical Data Depth
  DATA_HISTORY_CRIME: 'data.history.months.crime',
  DATA_HISTORY_LAND_REGISTRY: 'data.history.months.land_registry',
  DATA_HISTORY_PRICE: 'data.history.months.price_history',
  DATA_HISTORY_ONS_RENTAL: 'data.history.months.ons_rental',

  // Namespaced Deal Detail Sections
  DETAIL_PROPERTY_OVERVIEW: 'deal_detail.section.property_overview',
  DETAIL_STRATEGY_PILLS: 'deal_detail.section.strategy_pills',
  DETAIL_STRATEGY_SCORE: 'deal_detail.section.strategy_score',
  DETAIL_STRATEGY_ANALYSIS: 'deal_detail.section.strategy_analysis',
  DETAIL_COST_BREAKDOWN: 'deal_detail.section.cost_breakdown',
  DETAIL_LOCATION: 'deal_detail.section.location',
  DETAIL_TRANSPORT: 'deal_detail.section.transport',
  DETAIL_CRIME: 'deal_detail.section.crime',
  DETAIL_FLOOD: 'deal_detail.section.flood',
  DETAIL_LAND_REGISTRY: 'deal_detail.section.land_registry',
  DETAIL_FLOORPLANS: 'deal_detail.section.floorplans',
  DETAIL_ORIGINAL_LISTING: 'deal_detail.section.original_listing',
  DETAIL_AGENT: 'deal_detail.section.agent',
  DETAIL_FULL_ADDRESS: 'deal_detail.section.full_address',

  // Namespaced Other
  FILTERS_ADVANCED: 'filters.advanced',
  FILTERS_SAVED_PRESETS: 'filters.saved_presets',
  NOTIFICATIONS_EMAIL: 'notifications.email',
  NOTIFICATIONS_WHATSAPP: 'notifications.whatsapp',
  MAP_BASIC: 'map.amenities.basic',
  MAP_TRANSPORT: 'map.amenities.transport',
  MAP_SCHOOLS: 'map.amenities.schools',
  MAP_CRIME: 'map.amenities.crime',
  MAP_FULL: 'map.amenities.full',
  API: 'api.access',
  PORTFOLIO: 'portfolio.tracking',
  OFFMARKET: 'offmarket.deals',
} as const;

export type FeatureId = (typeof FEATURE_IDS)[keyof typeof FEATURE_IDS];

// ============================================================================
// Policy Hooks (Phase 7b)
// Build content policies from resolved capabilities for PDF/CSV export.
// ============================================================================

/** Capability IDs used to build PDF policy */
const PDF_SECTION_CAPS = {
  property_overview: FEATURE_IDS.EXPORT_PDF_SECTION_PROPERTY_OVERVIEW,
  quality_score: FEATURE_IDS.EXPORT_PDF_SECTION_QUALITY_SCORE,
  strategy_analysis: FEATURE_IDS.EXPORT_PDF_SECTION_STRATEGY_ANALYSIS,
  location_analysis: FEATURE_IDS.EXPORT_PDF_SECTION_LOCATION_ANALYSIS,
  crime: FEATURE_IDS.EXPORT_PDF_SECTION_CRIME,
  transport: FEATURE_IDS.EXPORT_PDF_SECTION_TRANSPORT,
  flood: FEATURE_IDS.EXPORT_PDF_SECTION_FLOOD,
  land_registry: FEATURE_IDS.EXPORT_PDF_SECTION_LAND_REGISTRY,
  original_listing: FEATURE_IDS.EXPORT_PDF_SECTION_ORIGINAL_LISTING,
  disclaimer: FEATURE_IDS.EXPORT_PDF_SECTION_DISCLAIMER,
} as const;

/**
 * Builds a PdfPolicy from the user's resolved capabilities.
 * The caller passes the returned policy to exportPropertyPDF() —
 * the PDF generator never calls hooks or checks tiers directly.
 *
 * @example
 * ```tsx
 * const pdfPolicy = usePdfPolicy();
 * // ... in export handler:
 * await exportPropertyPDF(data, crime, poi, strategies, score, pdfPolicy);
 * ```
 */
export function usePdfPolicy(): PdfPolicy {
  const featureAccess = useAppSelector(selectFeatureAccess);

  return useMemo(() => {
    // Fail-closed: if feature access hasn't loaded yet, default to all sections
    // enabled (the export.pdf button gate already prevents unauthorized access).
    // Once loaded, respect the resolved capabilities.
    const features = featureAccess?.features ?? {};
    const isLoaded = !!featureAccess;

    // When loaded, respect each capability. When not loaded, default to true
    // because the parent export.pdf gate (Phase 7a) already blocks the button.
    const enabledSections = {} as PdfPolicy['enabledSections'];
    for (const [section, capId] of Object.entries(PDF_SECTION_CAPS)) {
      enabledSections[section as keyof typeof PDF_SECTION_CAPS] =
        isLoaded ? (features[capId]?.hasAccess ?? false) : true;
    }

    const maxPagesResult = features[FEATURE_IDS.EXPORT_PDF_MAX_PAGES];
    const maxPages = (isLoaded && maxPagesResult?.hasAccess)
      ? (maxPagesResult.value as number | null) ?? null
      : null;

    const unwatermarked =
      features[FEATURE_IDS.EXPORT_PDF_UNWATERMARKED]?.hasAccess ?? false;

    return { enabledSections, maxPages, unwatermarked };
  }, [featureAccess]);
}

/**
 * Returns the resolved CSV max rows limit from capabilities.
 * Returns null if the capability is not loaded yet.
 */
export function useCsvMaxRows(): number | null {
  const featureAccess = useAppSelector(selectFeatureAccess);

  return useMemo(() => {
    const result = featureAccess?.features?.[FEATURE_IDS.EXPORT_CSV_MAX_ROWS];
    if (!result?.hasAccess) return null;
    return (result.value as number) ?? null;
  }, [featureAccess]);
}
